import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

// Review status values shared between the filter dropdown and the SME review
// block. Maps onto questionevaluations.human_approval_status plus the new
// needs_revision value (see SPEC §10.2c).
export const REVIEW_STATUS_OPTIONS = [
  { value: "pending", label: "Pending Review" },
  { value: "needs_revision", label: "Needs Revision" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const REVIEW_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All review statuses" },
  ...REVIEW_STATUS_OPTIONS,
];

// question_type maps onto the BE ChallengeType enum (uppercase values).
export const QUESTION_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
  { value: "OPEN_RESPONSE", label: "Open Response" },
];

export const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active only" },
  { value: "inactive", label: "Inactive only" },
];

// Tri-state filter tokens accepted by the BE: all | yes | no | nodata.
export const TRIBOOL_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "nodata", label: "No Data" },
];

// True when the backend hasn't shipped the endpoint yet (404 / not found).
// Lets the UI degrade to an informative "endpoint pending" state instead of a
// hard error while the BE agent's PR is still in flight.
export const isEndpointMissing = (e) => {
  const status = e?.status;
  const detail = `${e?.detail || ""} ${e?.message || ""}`.toLowerCase();
  return (
    status === 404 ||
    status === 405 ||
    detail.includes("404") ||
    detail.includes("not found") ||
    detail.includes("method not allowed")
  );
};

const log = (message, e) =>
  logError(message, e, { category: LogCategory.ACTIVITY, activityType: "evaluator" });

// GET /admin/evaluator/questions — filters passed as query params.
export const fetchEvaluatorQuestions = async (filters, { page = 1, pageSize = 50 } = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "" || value === "all") return;
    params.set(key, value);
  });
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  const qs = params.toString();
  try {
    return await request(`admin/evaluator/questions${qs ? `?${qs}` : ""}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch evaluator questions", e);
    throw e;
  }
};

// GET /admin/evaluator/questions/{id}/ccss-relationships
export const fetchCcssRelationships = async (questionId) => {
  try {
    return await request(`admin/evaluator/questions/${questionId}/ccss-relationships`, {
      method: "GET",
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch CCSS relationships", e);
    throw e;
  }
};

// GET /admin/evaluator/lessons/{lesson_id}
export const fetchLesson = async (lessonId) => {
  try {
    return await request(`admin/evaluator/lessons/${lessonId}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch lesson detail", e);
    throw e;
  }
};

// PUT /admin/evaluator/questions/{id}/review — body {status, notes}
export const saveReview = async (questionId, { status, notes }) => {
  try {
    return await request(`admin/evaluator/questions/${questionId}/review`, {
      method: "PUT",
      payload: { status, notes },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to save evaluator review", e);
    throw e;
  }
};

// GET /admin/evaluator/sources — distinct item sources for the Item Source
// filter dropdown. Returns {sources: [...]}. Used to populate a Select instead
// of free-text entry; degrades to the text input if the endpoint 404s.
export const fetchEvaluatorSources = async () => {
  try {
    return await request("admin/evaluator/sources", { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch evaluator sources", e);
    throw e;
  }
};

// GET /admin/evaluator/batches — saved batches for the queue selector. Returns
// {batches: [{id, name, item_count}]} so the option value is the batch UUID,
// which is passed straight back as batch_id to the questions endpoint.
export const listEvaluationBatches = async () => {
  try {
    return await request("admin/evaluator/batches", { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to list evaluation batches", e);
    throw e;
  }
};

// ─── LC Alignment Review endpoints ────────────────────────────────────────────

// Form enum option lists shared between the API module and the review UI.

export const QUALITY_GATE_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "reject", label: "Reject" },
];

export const REJECTION_TAG_OPTIONS = [
  { value: "BAD_KEY", label: "Bad Key" },
  { value: "BAD_IMAGE", label: "Bad Image" },
  { value: "UNREADABLE", label: "Unreadable" },
  { value: "MISSING_CONTEXT", label: "Missing Context" },
];

// TJ's OKO2-3390 rubric labels for the LC Alignment dropdown (WIP, wording
// may still change; this is the single place to edit if so).
export const LC_ALIGNMENT_OPTIONS = [
  { value: "strong_alignment", label: "Strong Alignment" },
  { value: "aligned_but_more", label: "Aligned But More" },
  { value: "aligned_by_bypassable", label: "Aligned by Bypassable" },
  { value: "no_alignment", label: "No Alignment" },
];

export const CONFIDENCE_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

// GET /admin/evaluator/lc-review/batches → { batches: [{ name, item_count }] }
export const fetchLcReviewBatches = async () => {
  try {
    return await request("admin/evaluator/lc-review/batches", { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch LC review batches", e);
    throw e;
  }
};

// GET /admin/evaluator/lc-review/queue?batch_name=<name>
// → { batch_name, total, items: [{ pair_id, position, question_id, question_type, lc_id, standard_code, reviewed }] }
export const fetchLcReviewQueue = async (batchName) => {
  try {
    const qs = batchName ? `?batch_name=${encodeURIComponent(batchName)}` : "";
    return await request(`admin/evaluator/lc-review/queue${qs}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch LC review queue", e);
    throw e;
  }
};

// GET /admin/evaluator/lc-review/item?question_id=<uuid>&question_type=<type>&lc_id=<str>&standard_code=<str>
// → { question, lc, ccss, existing_review }
export const fetchLcReviewItem = async ({ questionId, questionType, lcId, standardCode }) => {
  try {
    const params = new URLSearchParams();
    if (questionId) params.set("question_id", questionId);
    if (questionType) params.set("question_type", questionType);
    if (lcId) params.set("lc_id", lcId);
    if (standardCode) params.set("standard_code", standardCode);
    return await request(`admin/evaluator/lc-review/item?${params.toString()}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch LC review item", e);
    throw e;
  }
};

// PUT /admin/evaluator/lc-review
// body: { question_id, question_type, lc_id, quality_gate, rejection_tag,
//         worked_solution, soft_flag, item_solve, lc_alignment,
//         annotator_confidence, annotator_reasoning }
// → saved LcAlignmentReviewOut
export const saveLcReview = async (payload) => {
  try {
    return await request("admin/evaluator/lc-review", {
      method: "PUT",
      payload,
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to save LC alignment review", e);
    throw e;
  }
};

// POST /admin/evaluator/lc-review/sheet-sync?batch_name=<name>
// Manual "Refresh to Sheet" fallback — same endpoint the every-30-minute
// Cloud Scheduler job hits. Omit batchName to export every batch.
// → { spreadsheet_id, sheet_name, rows_written, batch_name }
export const syncLcReviewToSheet = async (batchName) => {
  try {
    const qs = batchName ? `?batch_name=${encodeURIComponent(batchName)}` : "";
    return await request(`admin/evaluator/lc-review/sheet-sync${qs}`, { method: "POST" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to refresh LC review Sheet", e);
    throw e;
  }
};
